import { IconCheck, IconInfoCircle, IconPencil } from '@tabler/icons-react';
import { Badge } from '@zorro/design-system/v2/Badge';
import { Button } from '@zorro/design-system/v2/Button';
import { Card } from '@zorro/design-system/v2/Card';
import { Divider } from '@zorro/design-system/v2/Divider';
import { ThemeIcon } from '@zorro/design-system/v2/ThemeIcon';
import { Tooltip } from '@zorro/design-system/v2/Tooltip';
import { MissingDetailsIcon } from '@zorro/shared/assets';
import {
  Box,
  Center,
  Grid,
  Group,
  Icon,
  Stack,
  Text,
  brand,
} from '@zorro/web/design-system';
import { useAppRouter } from '@zorro/web/ui-utils';
import { ReactElement, ReactNode } from 'react';

import {
  AddEmployerStepType,
  getAddEmployerStepConfig,
} from '../AddEmployerStep/AddEmployerUtils';

export type ReviewItemProps =
  | {
      name: string;
      toolTipLabel?: ReactNode;
      value?: ReactNode | string | null;
      icon?: ReactElement;
      isRequired?: boolean;
      span?: number;
    }
  | {
      name: string;
      isSeparator: true;
    }
  | {
      name: string;
      title: string;
    };

const ReviewItem = (props: ReviewItemProps) => {
  if ('isSeparator' in props) {
    return <Divider w="100%" />;
  }

  if ('title' in props) {
    const { title } = props;
    return (
      <Grid.Col pl="2rem" pr="0.5rem">
        <Stack gap="xs">
          <Group gap="0.25rem" wrap="nowrap">
            <Text fw={600}>{title}</Text>
          </Group>
        </Stack>
      </Grid.Col>
    );
  }

  const {
    name,
    toolTipLabel,
    value,
    icon,
    isRequired = false,
    span = 4,
  } = props;

  const shouldMarkRed = isRequired && (!value || value === '-');

  return (
    <Grid.Col span={span} pl="2rem" pr="0.5rem">
      <Stack gap="xs">
        <Group gap="0.25rem" wrap="nowrap" h="30px">
          {icon && (
            <Box
              style={{
                width: 24,
                height: 24,
                marginLeft: '-0.1rem',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {icon}
            </Box>
          )}
          {toolTipLabel ? (
            <Group wrap="nowrap" gap="4">
              <Text c="zorroGray.4">
                {name}{' '}
                {isRequired ? (
                  shouldMarkRed ? (
                    <Text c="zorroFire.7">*</Text>
                  ) : (
                    '*'
                  )
                ) : (
                  ''
                )}
              </Text>
              <Tooltip label="Put a tentative window if you haven’t finalized it yet; you will be able to change the dates later">
                <Box pos="relative">
                  <Center>
                    <Icon
                      icon={IconInfoCircle}
                      color={brand.zorroGray400}
                      size="1.25rem"
                    />
                  </Center>
                </Box>
              </Tooltip>
            </Group>
          ) : (
            <Text c="zorroGray.4">
              {name}{' '}
              {isRequired ? (
                shouldMarkRed ? (
                  <Text c="zorroFire.7">*</Text>
                ) : (
                  '*'
                )
              ) : (
                ''
              )}
            </Text>
          )}
        </Group>
        <Text style={{ textOverflow: 'ellipsis', overflow: 'hidden' }}>
          {value || '-'}
        </Text>
      </Stack>
    </Grid.Col>
  );
};

type Props = {
  employerId: string;
  title: string;
  step: AddEmployerStepType;
  items: ReviewItemProps[];
  isValid: boolean;
  isDon: boolean;
};

export const ReviewCard = ({
  employerId,
  title,
  step,
  items,
  isValid,
  isDon,
}: Props) => {
  const { navigateToRoute } = useAppRouter();

  const { route, shouldShowMissingDetails } = getAddEmployerStepConfig(
    step,
    isDon
  );

  let icon: ReactElement | undefined = undefined;
  if (isValid) {
    icon = (
      <ThemeIcon color="green" bdrs="lg">
        <IconCheck />
      </ThemeIcon>
    );
  } else if (shouldShowMissingDetails) {
    icon = <MissingDetailsIcon />;
  }

  return (
    <Card
      variant="white-border"
      style={{ borderColor: 'var(--mantine-color-zorroGray-3)' }}
      radius="md"
      p={0}
    >
      <Group
        align="center"
        style={(theme) => ({
          borderBottom: `1px solid ${theme.colors.zorroGray[3]}`,
        })}
        wrap="nowrap"
        py="md"
        px="xl"
        gap={8}
      >
        {icon}
        <Text fw={600}>{title}</Text>
        {!isValid && (
          <Badge color={shouldShowMissingDetails ? 'red' : 'gray'}>
            Missing details
          </Badge>
        )}
        <Button
          variant="tertiary"
          onClick={() => navigateToRoute(route, [employerId])}
          size="md"
          ml="auto"
          leftSection={<Icon icon={IconPencil} />}
        >
          Edit
        </Button>
      </Group>

      <Grid my="md" styles={{ inner: { gap: '0.75rem 0' } }}>
        {items.map((item, index) => (
          // eslint-disable-next-line @eslint-react/no-array-index-key
          <ReviewItem key={`${item.name}_${index}`} {...item} />
        ))}
      </Grid>
    </Card>
  );
};
